/**
 * The thin seam between "running in a browser tab" and "running inside a
 * Capacitor shell".
 *
 * Everything native is reached through this module and nothing else, so the
 * web build never imports a Capacitor plugin and the native build never has to
 * special-case a missing one. Web implementations are honest no-ops rather
 * than throwing: a haptic tap that does nothing on desktop is correct.
 */
export interface Platform {
  readonly isNative: boolean;
  readonly name: 'web' | 'ios' | 'android';
  hapticTap(): void;
  hapticSuccess(): void;
  /** Push registration token, or null when push is unavailable. */
  registerForPush(): Promise<string | null>;
  lockPortrait(): Promise<void>;
  /** Safe-area insets in CSS pixels; zeros on the web. */
  safeAreaInsets(): { top: number; bottom: number; left: number; right: number };
}

/**
 * Vibrate, once the player has touched the page. Chrome refuses a vibration
 * before the first tap, click or key press and logs an intervention for each
 * one, and a bot's moves land before the player has done anything.
 */
function vibrate(pattern: number | number[]): void {
  const navigator = globalThis.navigator;
  /* A browser without `userActivation` vibrates as it always did. */
  const activation: UserActivation | undefined = navigator?.userActivation;
  if (activation !== undefined && !activation.hasBeenActive) return;
  navigator?.vibrate?.(pattern);
}

const webPlatform: Platform = {
  isNative: false,
  name: 'web',
  hapticTap() {
    vibrate(8);
  },
  hapticSuccess() {
    vibrate([12, 40, 18]);
  },
  registerForPush: () => Promise.resolve(null),
  lockPortrait: () => Promise.resolve(),
  safeAreaInsets: () => ({ top: 0, bottom: 0, left: 0, right: 0 }),
};

let active: Platform = webPlatform;

/** Called by the native bootstrap; the web build never touches it. */
export function installPlatform(platform: Platform): void {
  active = platform;
}

export function platform(): Platform {
  return active;
}

export { webPlatform };
